import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useGame } from '../context/GameContext';
import { NODE_TYPES } from '../game/map';
import { relicById } from '../game/relics';
import { classById } from '../game/classes';
import { CARDS, cardRarity } from '../game/cards';
import { BLESSINGS } from '../game/blessings';
import { RelicBadge, BlessingBadge } from '../components/RelicBadge';
import ActBadge from '../components/ActBadge';
import GhostHUD from '../components/GhostHUD';
import { Skull, Coins, ShoppingCart, Tent, HelpCircle, Swords, Crown, Heart, Ghost, User, Sparkles, Gift } from 'lucide-react';

const nodeIcon = {
  [NODE_TYPES.MONSTER]: Swords,
  [NODE_TYPES.ELITE]: Skull,
  [NODE_TYPES.SHOP]: ShoppingCart,
  [NODE_TYPES.REST]: Tent,
  [NODE_TYPES.EVENT]: HelpCircle,
  [NODE_TYPES.BOSS]: Crown,
  [NODE_TYPES.MID_BOSS]: Crown,
  [NODE_TYPES.SHRINE]: Sparkles,
  [NODE_TYPES.TREASURE]: Gift,
};

const nodeLabel = {
  [NODE_TYPES.MONSTER]: 'FIGHT',
  [NODE_TYPES.ELITE]: 'ELITE',
  [NODE_TYPES.SHOP]: 'SHOP',
  [NODE_TYPES.REST]: 'REST',
  [NODE_TYPES.EVENT]: '???',
  [NODE_TYPES.BOSS]: 'FINAL',
  [NODE_TYPES.MID_BOSS]: 'MID-BOSS',
  [NODE_TYPES.SHRINE]: 'SHRINE',
  [NODE_TYPES.TREASURE]: 'VAULT',
};

// v2.5 QoL: hover-preview description per node type
const nodeDesc = {
  [NODE_TYPES.MONSTER]: 'A regular enemy. Card + gold reward on victory.',
  [NODE_TYPES.ELITE]: 'Elite enemy — harder with a combat modifier. Better loot. FIGHT or SKIP for a curse.',
  [NODE_TYPES.MID_BOSS]: 'Mid-boss. Victory grants a permanent BLESSING pick.',
  [NODE_TYPES.BOSS]: 'FINAL BOSS. Multi-phase.',
  [NODE_TYPES.SHOP]: 'Buy cards, relics, or REROLL the shelf. Removal service available.',
  [NODE_TYPES.SHRINE]: 'Powerful boons — no fight, no cost.',
  [NODE_TYPES.REST]: 'Heal 30% HP OR upgrade a card.',
  [NODE_TYPES.EVENT]: 'Random encounter — risk/reward.',
  [NODE_TYPES.TREASURE]: 'Free chest — random loot from all cards.',
};

export default function MapScreen() {
  const { run, enterNode, setScreen, goToMenu, seedGhosts, playerName, activeCurse, ghostRun } = useGame();
  const [confirmAbandon, setConfirmAbandon] = useState(false);
  const currentRef = useRef(null);
  useEffect(() => {
    if (currentRef.current) {
      currentRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [run && run.floorIdx]);
  if (!run) return null;
  const { map, floorIdx, player } = run;
  const myTag = (playerName || '').toUpperCase();

  // Group ghosts by floor
  const ghostsByFloor = {};
  for (const g of seedGhosts || []) {
    if (g.player_name === myTag) continue; // skip self
    const f = g.floor_reached - 1;
    if (!ghostsByFloor[f]) ghostsByFloor[f] = [];
    ghostsByFloor[f].push(g);
  }

  // v1.7 POLISH: deck stats mini-strip
  const deckStats = (() => {
    const deck = player.deck || [];
    const stats = { total: deck.length, rare: 0, legendary: 0, curse: 0, attack: 0, skill: 0, power: 0, costSum: 0, costCount: 0 };
    for (const cid of deck) {
      const r = cardRarity(cid);
      if (r === 'rare') stats.rare++;
      if (r === 'legendary') stats.legendary++;
      const c = CARDS[cid] || {};
      if (c.curse) stats.curse++;
      if (c.type === 'attack') stats.attack++;
      else if (c.type === 'skill') stats.skill++;
      else if (c.type === 'power') stats.power++;
      if (typeof c.cost === 'number' && c.cost >= 0) { stats.costSum += c.cost; stats.costCount++; }
    }
    stats.avgCost = stats.costCount > 0 ? (stats.costSum / stats.costCount).toFixed(1) : '—';
    return stats;
  })();

  return (
    <div className="min-h-screen w-full flex flex-col relative" data-testid="map-screen">
      <div className="border-b-2 border-[color:var(--neon)] px-6 py-3 flex items-center justify-between bg-[#0a0a0a]">
        <div className="flex items-center gap-6 text-sm font-mono uppercase tracking-widest">
          <span className="text-[color:var(--text-dim)]">SEED</span>
          <span className="text-[color:var(--neon)] font-display font-bold" data-testid="run-seed">{run.seed}</span>
          <span className="text-[color:var(--text-dim)]">FLOOR</span>
          <span className="text-[color:var(--pink)] font-display font-bold" data-testid="current-floor">{floorIdx + 1}/{map.length}</span>
          <ActBadge floor={floorIdx + 1} testid="map-act-badge"/>
          {run.ascension > 0 && <span className="brutal-border-pink px-2 py-0.5 text-[10px] font-bold text-[color:var(--pink)]" data-testid="hud-ascension">A{run.ascension}</span>}
          {activeCurse && <span className="brutal-border-pink px-2 py-0.5 text-[10px] font-bold text-[color:var(--red)]" data-testid="hud-cursed"><Skull size={10} className="inline"/> CURSED</span>}
        </div>
        <div className="flex items-center gap-6 text-sm font-mono uppercase">
          <span className="flex items-center gap-2">
            <Heart size={16} className="text-[color:var(--neon)]" />
            <span data-testid="player-hp">{player.hp}/{player.maxHp}</span>
          </span>
          <span className="flex items-center gap-2">
            <Coins size={16} className="text-[color:var(--pink)]" />
            <span data-testid="player-gold">{player.gold}</span>
          </span>
          <button className="brutal-btn text-xs px-3 py-1" onClick={() => setScreen('deckview')} data-testid="view-deck-button">
            DECK ({player.deck.length})
          </button>
          <button className="brutal-btn-pink brutal-btn text-xs px-3 py-1" onClick={() => setConfirmAbandon(true)} data-testid="abandon-run-button">
            ABANDON
          </button>
        </div>
      </div>

      {/* v3.14 GHOST MODE: show your best run on this seed alongside the current pace */}
      {ghostRun && (
        <div className="px-6 py-2 border-b border-[color:var(--muted)] bg-[#050505] flex items-center justify-end">
          <GhostHUD ghost={ghostRun} currentFloor={floorIdx + 1} currentScore={run.score || 0} testid="map-ghost-hud" />
        </div>
      )}

      {/* v1.7 POLISH: Deck stats mini-strip */}
      <div className="px-6 py-1 border-b border-[color:var(--muted)] flex items-center gap-3 bg-[#050505] flex-wrap text-[10px] font-mono uppercase tracking-widest text-[color:var(--text-dim)]"
           data-testid="deck-stats-strip">
        <span data-testid="deck-stats-total">{deckStats.total} CARDS</span>
        <span className="text-[color:var(--text-dim)]">·</span>
        <span className="text-[color:var(--neon)]" data-testid="deck-stats-attack">{deckStats.attack} ATK</span>
        <span className="text-[color:var(--pink)]" data-testid="deck-stats-skill">{deckStats.skill} SKL</span>
        <span className="text-[color:var(--red)]" data-testid="deck-stats-power">{deckStats.power} PWR</span>
        <span className="text-[color:var(--text-dim)]">·</span>
        {deckStats.rare > 0 && <span className="text-[#ffd700]" data-testid="deck-stats-rare">★{deckStats.rare} RARE</span>}
        {deckStats.legendary > 0 && <span className="text-[color:var(--pink)]" data-testid="deck-stats-legendary">✧{deckStats.legendary} LGD</span>}
        {deckStats.curse > 0 && <span className="text-[color:var(--red)]" data-testid="deck-stats-curse">💀 {deckStats.curse} CURSE</span>}
        <span className="text-[color:var(--text-dim)]">·</span>
        <span data-testid="deck-stats-avg-cost">AVG COST {deckStats.avgCost}</span>
      </div>

      {/* Relics bar */}
      <div className="px-6 py-2 border-b-2 border-[color:var(--muted)] flex items-center gap-3 bg-[#080808] flex-wrap">
        <span className="text-xs uppercase tracking-widest text-[color:var(--text-dim)]">RELICS:</span>
        {player.relics.length === 0 && <span className="text-xs text-[color:var(--text-dim)]">none</span>}
        {player.relics.map(rid => (
          <RelicBadge
            key={rid}
            relicId={rid}
            pickupFloor={run.relicPickupFloors?.[rid]}
            triggers={run.relicTriggers?.[rid]}
            testid={`relic-${rid}`}
          />
        ))}
        {seedGhosts.length > 0 && (
          <div className="ml-auto flex items-center gap-2 brutal-border-dim px-2 py-1 text-xs" data-testid="ghosts-indicator">
            <Ghost size={14} className="text-[color:var(--pink)]"/>
            <span className="text-[color:var(--text-dim)] uppercase">GHOSTS ON THIS SEED:</span>
            <span className="text-[color:var(--neon)] font-bold">{seedGhosts.length}</span>
          </div>
        )}
      </div>

      {/* v2.1 BOSS BLESSINGS bar */}
      {player._blessings && player._blessings.length > 0 && (
        <div className="px-6 py-2 border-b-2 border-[color:var(--pink)] flex items-center gap-3 bg-[#12060c] flex-wrap" data-testid="blessings-bar">
          <span className="text-xs uppercase tracking-widest text-[color:var(--pink)]">BLESSINGS:</span>
          {player._blessings.map(bid => (
            <BlessingBadge
              key={bid}
              blessingId={bid}
              pickupFloor={run.blessingPickupFloors?.[bid]}
              testid={`blessing-${bid}`}
            />
          ))}
        </div>
      )}

      {/* Map */}
      <div className="flex-1 overflow-y-auto px-6 py-10">
        <div className="max-w-3xl mx-auto flex flex-col-reverse gap-8">
          {map.map((floor, fIdx) => (
            <div key={fIdx} ref={fIdx === floorIdx ? currentRef : null} className="relative flex items-center justify-center gap-8">
              <div className="absolute left-0 text-xs font-mono uppercase tracking-widest text-[color:var(--text-dim)]">
                F{fIdx + 1}
              </div>
              {floor.map((node, cIdx) => {
                const Icon = nodeIcon[node.type];
                const available = fIdx === floorIdx;
                const completed = fIdx < floorIdx;
                const cls = `node ${available ? 'available' : ''} ${completed ? 'completed' : ''}`;
                return (
                  <div key={cIdx} className="flex flex-col items-center gap-1 relative group" data-testid={`map-node-wrap-f${fIdx}-c${cIdx}`}>
                    <div
                      className={cls}
                      onClick={() => available && enterNode(fIdx, cIdx)}
                      data-testid={`map-node-f${fIdx}-c${cIdx}`}
                      data-nodetype={node.type}
                    >
                      <Icon size={22} />
                    </div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--text-dim)]">
                      {nodeLabel[node.type]}
                    </div>
                    {/* v2.5 QoL: hover preview */}
                    <div
                      className="absolute left-1/2 -translate-x-1/2 top-full mt-1 z-20 opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-150 min-w-[220px] max-w-[280px] brutal-border-dim bg-[#0a0a0a] p-2 text-[10px] uppercase font-mono leading-relaxed"
                      style={{ borderColor: 'var(--neon)', boxShadow: '2px 2px 0 var(--neon)' }}
                      data-testid={`map-node-preview-f${fIdx}-c${cIdx}`}
                    >
                      <div className="text-[color:var(--neon)] font-bold">{nodeLabel[node.type]} · FLOOR {fIdx + 1}</div>
                      <div className="text-[color:var(--text-dim)] mt-1 normal-case tracking-normal">{nodeDesc[node.type]}</div>
                    </div>
                  </div>
                );
              })}
              {/* Ghosts died at this floor */}
              {ghostsByFloor[fIdx]?.length > 0 && (
                <div className="absolute right-0 flex items-center gap-1" data-testid={`ghosts-floor-${fIdx}`}>
                  {ghostsByFloor[fIdx].slice(0, 3).map((g, i) => (
                    <div key={i} className="brutal-border-dim px-1.5 py-0.5 bg-[#0a0a0a] flex items-center gap-1 text-[10px]" title={`${g.player_name} · ${g.score} pts`}>
                      <Ghost size={10} className="text-[color:var(--pink)]"/>
                      <span className="font-bold text-white uppercase">{g.player_name}</span>
                    </div>
                  ))}
                  {ghostsByFloor[fIdx].length > 3 && (
                    <div className="text-[10px] uppercase text-[color:var(--text-dim)]">+{ghostsByFloor[fIdx].length - 3}</div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* v1.8 DEPTH: Confirm abandon modal */}
      {confirmAbandon && (
        <div className="pause-overlay" onClick={() => setConfirmAbandon(false)} data-testid="confirm-abandon-overlay">
          <div className="pause-panel" onClick={e => e.stopPropagation()} style={{ borderColor: '#ff2b2b', boxShadow: '8px 8px 0 #ff2b2b' }}>
            <div className="text-[color:var(--red)] font-mono uppercase tracking-widest text-xs">⚠ ABANDON RUN</div>
            <div className="font-display font-black text-3xl text-white mt-2 mb-1">SEND IT BACK TO THE VOID?</div>
            <div className="text-xs uppercase font-mono text-[color:var(--text-dim)] mb-6">Your progress this run will be lost. Save/Resume also cleared.</div>
            <div className="flex gap-3">
              <button className="brutal-btn text-sm flex-1" onClick={() => setConfirmAbandon(false)} data-testid="confirm-abandon-cancel">
                KEEP GOING
              </button>
              <button className="brutal-btn-pink brutal-btn text-sm flex-1" style={{ borderColor: '#ff2b2b', boxShadow: '4px 4px 0 #ff2b2b' }} onClick={() => { setConfirmAbandon(false); goToMenu(); }} data-testid="confirm-abandon-yes">
                ABANDON RUN
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
